import styles from "./uploaded-photos-grid.module.css";
import { PhotoWithCoordinates } from "PosterEditor/model/poster-project";
import { useUploadsStore } from "entity-store/uploads-store/uploads-store";
import { useState, useEffect } from "react";
import { CheckButton } from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import {
  DropdownOptionsMenu,
  DropdownOptionsMenuItem as DropdownItem,
} from "PosterEditor/common-components/dropdown-menu/dropdown-options-menu";
import { UploadPreview } from "components/common/upload-preview";
import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { DownloadAppInfoBox } from "../../map-labels/download-app-info-box";

interface UploadedPhotosGridProps {
  onLoadURLs: (urls: string[]) => void;
  onClickImage?: (url: string) => void;
}

export const UploadedPhotosGrid = (props: UploadedPhotosGridProps) => {
  const { imageURLsInUploadsFolder, deleteImageWithURL, deleteImagesWithURLs } =
    useUploadsStore();
  const [urls, setURLs] = useState<string[]>([]);
  const [photosWithCoordinates, setPhotosWithCoordinates] = useState<
    PhotoWithCoordinates[]
  >([]);
  const {
    createStyledPointFromPhoto,
    styledPointExistsForPhoto,
    removeStyledPointForPhoto,
  } = usePosterProject();

  useEffect(() => {
    // Fetch the image URLs from the uploads folder
    const fetchImageURLs = async () => {
      const urls = await imageURLsInUploadsFolder();
      setURLs(urls);
      props.onLoadURLs(urls);
    };
    fetchImageURLs();
  }, []);

  useEffect(() => {
    // Log the photos with coordinates whenever they change
  }, [photosWithCoordinates]);

  const deletePhoto = (url: string) => {
    deleteImageWithURL(url)
      .then(() => {
        setURLs((prev) => prev.filter((u) => u !== url));
        setPhotosWithCoordinates((prev) => prev.filter((p) => p.url !== url));
      })
      .catch((error) => {
        console.error("Error deleting photo:", error);
      });
  };

  const removeLabelForPhoto = (photo: PhotoWithCoordinates) => {
    removeStyledPointForPhoto(photo.url);
  };

  const applyAllPhotos = async () => {
    const photosToApply = photosWithCoordinates.filter(
      (photo) => !styledPointExistsForPhoto(photo.url)
    );

    try {
      const createdPoints = await Promise.all(
        photosToApply.map((photo) => createStyledPointFromPhoto(photo))
      );

      const allCoordinates = createdPoints.map((point) => point.coordinate);

      console.log(
        "All styled points applied with coordinates:",
        allCoordinates
      );
      // You can also do something further with `allCoordinates` here.
    } catch (error) {
      console.error("Error applying photos:", error);
    }
  };

  const applyPhoto = async (photo: PhotoWithCoordinates) => {
    try {
      const styledPoint = await createStyledPointFromPhoto(photo);
      return styledPoint;
    } catch (error) {
      console.error("Error applying photo:", error);
      throw error;
    }
  };

  const deleteAllPhotos = () => {
    const urlsToDelete = photosWithCoordinates.map((p) => p.url);
    deleteImagesWithURLs(urlsToDelete)
      .then(() => {
        setURLs([]);
        setPhotosWithCoordinates([]);
      })
      .catch((error) => {
        console.error("Error deleting all photos:", error);
      });
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionBody}>
        <div className={styles.imagePreviewGrid}>
          {urls.map((url, index) => (
            <div key={index}>
              {props.onClickImage ? (
                <SinglePhotoPreview
                  url={url}
                  onClick={() => props.onClickImage?.(url)}
                />
              ) : (
                <SinglePhotoPreview
                  url={url}
                  isApplied={styledPointExistsForPhoto(url)}
                  didLoadCoordinates={(coordinates) => {
                    console.log(
                      "Loaded coordinates for photo:",
                      url,
                      coordinates
                    );
                    setPhotosWithCoordinates((prev) => [
                      ...prev,
                      { url, coordinates },
                    ]);
                  }}
                  onClickApplyAll={applyAllPhotos}
                  onClickApplyOne={() => {
                    const photo = photosWithCoordinates.find(
                      (p) => p.url === url
                    );
                    if (photo) {
                      applyPhoto(photo);
                    }
                  }}
                  onClickDeleteOne={() => {
                    deletePhoto(url);
                  }}
                  onClickDeleteAll={() => {
                    deleteAllPhotos();
                  }}
                  onClickRemoveLabel={() => {
                    const photo = photosWithCoordinates.find(
                      (p) => p.url === url
                    );
                    if (photo) {
                      removeLabelForPhoto(photo);
                    }
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EmptyContent = () => {
  return (
    <div className={styles.sectionContent}>
      <h1>No photos uploaded</h1>
      <p>
        Drop photos right where you took them: upload right from our iOS app.
      </p>
    </div>
  );
};

interface SinglePhotoPreviewProps {
  url: string;
  onClick?: () => void;
  // Menu-related props (only needed if onClick is not provided)
  isApplied?: boolean;
  didLoadCoordinates?: (coordinates: Coordinate) => void;
  onClickRemoveLabel?: () => void;
  onClickApplyOne?: () => void;
  onClickDeleteOne?: () => void;
  onClickApplyAll?: () => void;
  onClickDeleteAll?: () => void;
}
const SinglePhotoPreview = (props: SinglePhotoPreviewProps) => {
  const SinglePhotoElement = () => {
    return (
      <div className={styles.singlePhotoStack}>
        <div className={styles.photo}>
          <UploadPreview
            imageUrl={props.url}
            didLoadGPSCoordinates={props.didLoadCoordinates ?? (() => {})}
          />
        </div>
        {props.isApplied && (
          <div className={styles.checkIcon}>
            <CheckButton isSelected={props.isApplied} onClick={() => {}} />
          </div>
        )}
      </div>
    );
  };

  // If onClick is provided, just render a simple clickable button
  if (props.onClick) {
    return (
      <button onClick={props.onClick} className={styles.singlePhotoPreview}>
        {SinglePhotoElement()}
      </button>
    );
  }

  // Otherwise, render with dropdown menu
  let dropdownOptions: DropdownItem[];

  if (props.isApplied) {
    dropdownOptions = [
      { id: "removeLabel", label: "Remove label", iconName: "REMOVE" },
      { id: "delete", label: "Delete photo", iconName: "BIN" },
    ];
  } else {
    dropdownOptions = [
      { id: "apply", label: "Apply as label", iconName: "PHOTO-LABEL" },
      { id: "applyAll", label: "Apply all as labels", iconName: "PHOTO-LABEL" },
      {
        id: "delete",
        label: "Delete photo",
        iconName: "BIN",
        meta: "Label will not be removed",
      },
      {
        id: "deleteAll",
        label: "Delete ALL",
        iconName: "BIN",
        meta: "Labels will not be removed",
      },
    ];
  }

  const onSelectMenuOption = (option: DropdownItem) => {
    if (option.id === "apply") {
      props.onClickApplyOne?.();
    } else if (option.id === "applyAll") {
      props.onClickApplyAll?.();
    } else if (option.id === "delete") {
      props.onClickDeleteOne?.();
    } else if (option.id === "deleteAll") {
      props.onClickDeleteAll?.();
    } else if (option.id === "removeLabel") {
      props.onClickRemoveLabel?.();
    }
  };

  return (
    <div className={styles.singlePhotoPreview}>
      <DropdownOptionsMenu
        options={dropdownOptions}
        labelComponent={SinglePhotoElement()}
        showChevron={false}
        alignment="center"
        onSelectOption={(option) => {
          onSelectMenuOption(option);
        }}
      />
    </div>
  );
};
